import React, { useCallback, useEffect, useRef, useState } from 'react';
import {
    Alert,
    Autocomplete,
    Avatar,
    Box,
    Button,
    CircularProgress,
    Dialog,
    DialogActions,
    DialogContent,
    DialogTitle,
    IconButton,
    MenuItem,
    Stack,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    TextField,
    Tooltip,
    Typography,
} from '@mui/material';
// Stessa icona piena degli altri elenchi: sulla stessa schermata convivevano un
// cestino contornato qui e uno pieno nelle due tabelle accanto, e due disegni per
// lo stesso gesto fanno cercare la differenza che non c'e'.
import DeleteIcon from '@mui/icons-material/Delete';
import PersonAddAltIcon from '@mui/icons-material/PersonAddAlt';
import groupService from '../../services/groupService';
import iniziali from '../common/iniziali';
import userAdminService from '../../services/userAdminService';
import useCan from '../../hooks/useCan';
import ConfirmDialog from '../ConfirmDialog';
import { useOptionalPermissions } from '../../context/permissionContextCore';
import { CONTENITORE_TABELLA, TESTATA_TABELLA } from '../common/stiliTabella';

// Quante persone proporre mentre si scrive: abbastanza per trovare chi si cerca, poche
// abbastanza da non tornare a scaricare il realm (B14).
const PROPOSTE_PERSONA = 20;

const RICERCA_DEBOUNCE_MS = 300;

export const STATO_VUOTO_MEMBRI =
    'Nessuno ha un ruolo in questo gruppo. Finché non c’è nessuno, il gruppo non cambia ' +
    'ciò che vede alcuna persona.';

/**
 * Chi sta in un gruppo, e chi ci si aggiunge.
 *
 * <p>Sta qui e non solo nella vista utente perche' la pagina del gruppo deve bastare a se'
 * stessa: creare un reparto — gruppo, code, tipi, persone — e' un compito solo, e farlo a
 * salti fra due schermate e' la ragione per cui la separazione fra Gruppi e Ruoli sembrava
 * pedante. Il salto non nasceva dalla distanza fra le pagine ma dal fatto che questa era
 * incompleta.</p>
 *
 * <p><b>Non e' un secondo percorso di scrittura.</b> Concedere qui chiama lo stesso
 * `POST /v1/users/{sub}/grants` della vista utente, governato dallo stesso `users.update`:
 * due schermate sulla stessa API non sono due strade, sono due porte. Chi ha `groups.update`
 * ma non `users.update` vede i membri e non il pulsante — il confine lo disegnano i permessi,
 * senza che questa pagina inventi una regola sua.</p>
 */
const GroupMembersPanel = ({ groupId, groupLabel }) => {
    const can = useCan();
    // Chi concede o revoca puo' essere anche il membro: si rileggono i propri permessi.
    const refreshPermissions = useOptionalPermissions()?.refresh;
    // `users.update` e non `groups.update`: concedere un ruolo a una persona e' una scrittura
    // sulla persona, e passa dallo stesso permesso ovunque la si faccia. Chi amministra il
    // contenuto del gruppo ma non le persone vede i membri e non il pulsante.
    const puoConcedere = can('users.update');

    const [membri, setMembri] = useState([]);
    const [caricamento, setCaricamento] = useState(false);
    const [errore, setErrore] = useState(null);
    // Revocare toglie subito l'accesso alla persona: si chiede conferma prima
    const [daRevocare, setDaRevocare] = useState(null);
    const [revocando, setRevocando] = useState(false);
    const ultimoDaRevocare = useRef(null);
    if (daRevocare) ultimoDaRevocare.current = daRevocare;

    const [dialogo, setDialogo] = useState(false);
    const [utenti, setUtenti] = useState([]);
    const [persona, setPersona] = useState(null);
    const [cercaPersona, setCercaPersona] = useState('');
    const [cercando, setCercando] = useState(false);
    const debounceRef = useRef(null);
    const generazioneRef = useRef(0);
    const [ruoli, setRuoli] = useState([]);
    const [scelta, setScelta] = useState({ sub: '', roleId: '' });
    const [salvataggio, setSalvataggio] = useState(false);

    const carica = useCallback(async () => {
        if (!groupId) return;
        setCaricamento(true);
        setErrore(null);
        try {
            setMembri(await groupService.membri(groupId));
        } catch (e) {
            setErrore('Impossibile leggere i membri del gruppo.');
        } finally {
            setCaricamento(false);
        }
    }, [groupId]);

    useEffect(() => {
        carica();
    }, [carica]);

    // Le persone si cercano sul backend mentre si scrive, e non si scaricano tutte
    // all'apertura: su un realm grande il menu era lento da aprire e impossibile da scorrere.
    // Solo le attive: concedere un ruolo a chi non puo' entrare non serve a nessuno.
    const cercaPersone = useCallback((testo) => {
        const generazione = ++generazioneRef.current;
        setCercando(true);
        userAdminService
            .listUsers({ q: testo, stato: 'attivi', pagina: 0, dimensione: PROPOSTE_PERSONA })
            .then((data) => {
                if (generazioneRef.current === generazione) {
                    setUtenti((data && data.righe) || []);
                }
            })
            .catch(() => {
                if (generazioneRef.current === generazione) {
                    setErrore('Impossibile leggere gli utenti.');
                }
            })
            .finally(() => {
                if (generazioneRef.current === generazione) {
                    setCercando(false);
                }
            });
    }, []);

    useEffect(
        () => () => {
            if (debounceRef.current) {
                clearTimeout(debounceRef.current);
            }
        },
        [],
    );

    const handleCercaPersona = (event, valore, motivo) => {
        setCercaPersona(valore);
        // Scegliere una voce riscrive il testo con il suo nome: non e' una nuova ricerca.
        if (motivo === 'reset') {
            return;
        }
        if (debounceRef.current) {
            clearTimeout(debounceRef.current);
        }
        debounceRef.current = setTimeout(() => cercaPersone(valore.trim()), RICERCA_DEBOUNCE_MS);
    };

    const apriDialogo = async () => {
        setScelta({ sub: '', roleId: '' });
        setPersona(null);
        setCercaPersona('');
        setDialogo(true);
        cercaPersone('');
        try {
            setRuoli(await userAdminService.listRoles());
        } catch (e) {
            setErrore('Impossibile leggere i ruoli.');
        }
    };

    const concedi = async () => {
        setSalvataggio(true);
        try {
            await userAdminService.grant(scelta.sub, scelta.roleId, groupId);
            setDialogo(false);
            refreshPermissions?.();
            await carica();
        } catch (e) {
            setErrore('Concessione non riuscita.');
        } finally {
            setSalvataggio(false);
        }
    };

    const revoca = async (membro) => {
        setRevocando(true);
        try {
            await userAdminService.revokeGrant(membro.userSub, membro.roleId, groupId);
            refreshPermissions?.();
            await carica();
        } catch (e) {
            setErrore('Revoca non riuscita.');
        } finally {
            setRevocando(false);
            setDaRevocare(null);
        }
    };

    const messaggioRevoca = (m) => (m
        ? `Togliere a ${m.userDisplayName || m.userEmail || m.userSub} il ruolo ` +
          `«${m.roleLabel}» nel gruppo «${groupLabel}»? La persona perde subito i permessi ` +
          'che quel ruolo le dava in questo gruppo.'
        : '');

    return (
        <Box>
            <Stack direction="row" alignItems="center" justifyContent="space-between" sx={{ minHeight: 40 }}>
                <Typography variant="h6">Membri</Typography>
                {puoConcedere && (
                    <Button
                        size="small"
                        startIcon={<PersonAddAltIcon />}
                        onClick={apriDialogo}
                    >
                        Aggiungi persona
                    </Button>
                )}
            </Stack>

            {errore && <Alert severity="error" sx={{ mt: 1 }}>{errore}</Alert>}

            {/* Una tabella e non pillole, con le stesse colonne della vista Utenti: chi legge
                qui sta facendo lo stesso lavoro che fa la', e due rappresentazioni diverse
                dello stesso fatto costringono a reimparare la schermata. La colonna Ruolo
                resta separata dalla persona perche' la stessa persona puo' comparire due
                volte, con due ruoli, e in una pillola sola sembrerebbe una riga doppia. */}
            <TableContainer {...CONTENITORE_TABELLA} sx={{ mt: 2 }}>
                <Table size="small">
                    <TableHead>
                        <TableRow sx={TESTATA_TABELLA}>
                            <TableCell>Persona</TableCell>
                            <TableCell>Ruolo nel gruppo</TableCell>
                            <TableCell align="right" />
                        </TableRow>
                    </TableHead>
                    <TableBody>
                        {caricamento && (
                            <TableRow>
                                <TableCell colSpan={3}>
                                    <CircularProgress size={18} />
                                </TableCell>
                            </TableRow>
                        )}
                        {!caricamento && membri.length === 0 && (
                            <TableRow>
                                {/* Testo secondario e non un Alert: un riquadro colorato a
                                    tutta larghezza dentro una cella si legge come una fascia
                                    grigia sotto l'intestazione, e nessun'altra tabella della
                                    sezione segnala cosi' l'assenza di righe. */}
                                <TableCell colSpan={3}>
                                    <Typography variant="body2" color="text.secondary">
                                        {STATO_VUOTO_MEMBRI}
                                    </Typography>
                                </TableCell>
                            </TableRow>
                        )}
                        {!caricamento && membri.map((m) => (
                            <TableRow key={`${m.userSub}-${m.roleId}`} hover>
                                <TableCell>
                                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                        <Avatar
                                            sx={{
                                                width: 32,
                                                height: 32,
                                                bgcolor: 'primary.light',
                                                color: 'primary.main',
                                                fontSize: '0.8rem',
                                            }}
                                        >
                                            {iniziali(m.userDisplayName || m.userEmail || m.userSub)}
                                        </Avatar>
                                        <Box>
                                            <Typography variant="body2">
                                                {m.userDisplayName || m.userEmail || m.userSub}
                                            </Typography>
                                            <Typography variant="caption" color="text.secondary">
                                                {m.userEmail}
                                            </Typography>
                                        </Box>
                                    </Box>
                                </TableCell>
                                <TableCell>
                                    <Typography variant="body2">{m.roleLabel}</Typography>
                                    <Typography variant="caption" color="text.secondary">
                                        {m.roleCode}
                                    </Typography>
                                </TableCell>
                                <TableCell align="right">
                                    {puoConcedere && (
                                        <Tooltip title={`Revoca ${m.roleLabel} su ${groupLabel}`}>
                                            <IconButton size="small" color="error" onClick={() => setDaRevocare(m)}>
                                                <DeleteIcon fontSize="small" />
                                            </IconButton>
                                        </Tooltip>
                                    )}
                                </TableCell>
                            </TableRow>
                        ))}
                    </TableBody>
                </Table>
            </TableContainer>

            <Dialog open={dialogo} onClose={() => setDialogo(false)} fullWidth>
                <DialogTitle>Aggiungi una persona a {groupLabel}</DialogTitle>
                <DialogContent>
                    <Stack spacing={2} sx={{ mt: 1 }}>
                        <Autocomplete
                            size="small"
                            options={utenti}
                            value={persona}
                            inputValue={cercaPersona}
                            onInputChange={handleCercaPersona}
                            onChange={(e, scelto) => {
                                setPersona(scelto);
                                setScelta((p) => ({ ...p, sub: scelto ? scelto.sub : '' }));
                            }}
                            // Il filtro lo fa il backend: rifiltrare qui scarterebbe chi
                            // corrisponde per email quando l'etichetta mostra il nome.
                            filterOptions={(opzioni) => opzioni}
                            isOptionEqualToValue={(a, b) => a.sub === b.sub}
                            getOptionLabel={(u) => u.displayName || u.email || u.sub}
                            loading={cercando}
                            loadingText="Ricerca…"
                            noOptionsText="Nessuna persona attiva trovata"
                            renderOption={(props, u) => (
                                <li {...props} key={u.sub}>
                                    <Box>
                                        <Typography variant="body2">
                                            {u.displayName || u.email || u.sub}
                                        </Typography>
                                        {u.email && (
                                            <Typography variant="caption" color="text.secondary">
                                                {u.email}
                                            </Typography>
                                        )}
                                    </Box>
                                </li>
                            )}
                            renderInput={(params) => (
                                <TextField
                                    {...params}
                                    label="Persona"
                                    placeholder="Cerca per nome o email"
                                />
                            )}
                        />
                        <TextField
                            select
                            size="small"
                            label="Ruolo"
                            value={scelta.roleId}
                            helperText="Il ruolo dice cosa può fare; il gruppo, su cosa."
                            onChange={(e) => setScelta((p) => ({ ...p, roleId: e.target.value }))}
                        >
                            {ruoli.map((r) => (
                                <MenuItem key={r.id} value={r.id}>{r.label}</MenuItem>
                            ))}
                        </TextField>
                    </Stack>
                </DialogContent>
                <DialogActions>
                    <Button onClick={() => setDialogo(false)}>Annulla</Button>
                    <Button
                        variant="contained"
                        disabled={!scelta.sub || !scelta.roleId || salvataggio}
                        onClick={concedi}
                    >
                        Concedi
                    </Button>
                </DialogActions>
            </Dialog>

            <ConfirmDialog
                open={Boolean(daRevocare)}
                title="Revoca ruolo"
                message={messaggioRevoca(ultimoDaRevocare.current)}
                confirmLabel="Revoca"
                busy={revocando}
                onConfirm={() => revoca(daRevocare)}
                onClose={() => setDaRevocare(null)}
            />
        </Box>
    );
};

export default GroupMembersPanel;
